import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import intlShape from '@/shared/utils/intlShape';
import CollapsablePanel from '@/shared/components/CollapablePanel';
import AssignedToSelector from '@/shared/components/input/AssignedToSelector';
import ExecutionDurationInput from '@/shared/components/input/ExecutionDurationInput';
import dataCacheService from '@/shared/services/DataCacheService';
import enumCache from '@/shared/services/EnumCacheService';
import { dynamicSort } from '@/shared/utils/dynamicSort';

import OkCancelModal from '@/shared/components/OkCancelModal';
import moment from 'moment';
import api from '@/shared/api';
import DatePicker from '@/shared/components/input/DatePicker';
import DayMonthPicker from '@/shared/components/input/DayMonthPicker';
import { listItemMsg } from '@/shared/components/FormattedListValue';
import classNames from 'classnames';
import Checkbox from '@/shared/components/input/Checkbox';
import {
  validateDateRange,
  validateEndDate,
  isBeforeLastYear,
} from '@/shared/utils/validationHelpers';
import EnumSelectV2 from '@/shared/components/input/EnumSelectV2';
import config from '../../../../config';
import messages from '../relationOverviewMessages';
import AuthService, { functionalities, privileges } from '@/shared/services/AuthService';

export default class UpdateTaskScheduleModal extends Component {
  state = {
    planningStart: moment(), // Set start date to today
    planningStop: null,
    scheduleDate: null,
    templates: [],
    action: 'add',
    deletePlannedTasks: false,
    deviationsCollapsed: true,
  };
  static propTypes = {
    show: PropTypes.bool.isRequired,
    intl: intlShape.isRequired,
    onSave: PropTypes.func.isRequired,
    onClose: PropTypes.func.isRequired,
    relationType: PropTypes.number,
  };
  static defaultProps = {
    relationType: null,
  };

  actionChanged = event => {
    const action = event.target.value;
    this.setState({
      action,
    });
  };

  retrievePossibleDeviationsForTemplate = templateId => {
    const promise = templateId
      ? api.base.get(
          `${config.appUrls.tasks}/api/TaskSchedules/deviationpossibilities/${templateId}`,
        )
      : Promise.resolve({ data: null });

    return promise.then(({ data }) => data.sort(dynamicSort('-isMainTaskDeviation')));
  };

  retrieveTaskTemplates = () => {
    const { intl, relationType } = this.props;

    api.base
      .get(`${config.appUrls.tasks}/api/TaskTemplates/Schedules`, {
        params: { relationType: relationType },
      })
      .then(({ data }) => {
        const sortOnLabel = (a, b) => (a.label > b.label) - (a.label < b.label);
        const translatedTemplates = data
          .map(t => ({
            ...t,
            label: intl.formatMessage(listItemMsg('taskTemplate', t.id, t.name)), // Map translation to label property
            periodicities: t.periodicities
              .map(p => ({
                ...p,
                label: intl.formatMessage(listItemMsg('taskperiodicity', p.id, p.name)), // Map translation to label property
              }))
              .sort(sortOnLabel),
          }))
          .sort(sortOnLabel);

        let selectedTemplate = null;
        let selectedPeriodicity = null;
        if (translatedTemplates && translatedTemplates.length > 0) {
          [selectedTemplate] = translatedTemplates;
          [selectedPeriodicity] = selectedTemplate.periodicities;
        }

        this.retrievePossibleDeviationsForTemplate(selectedTemplate?.id).then(deviations => {
          this.setState(state => ({
            ...state,
            templates: translatedTemplates,
            selectedTemplate,
            selectedPeriodicity,
            deviations,
          }));
        });
      });
  };

  componentDidMount() {
    const { intl } = this.props;
    this.retrieveTaskTemplates();
    enumCache.getTranslated('employeeprofile', intl).then(employeeProfiles => {
      this.setState({ employeeProfiles });
    });
  }

  templateChanged = event => {
    const { templates } = this.state;
    const templateId = event.target.value;
    const template = templates.find(x => x.id === templateId);

    let selectedPeriodicity = null;

    if (template && template.periodicities) {
      [selectedPeriodicity] = template.periodicities;
    }

    this.retrievePossibleDeviationsForTemplate(template?.id).then(deviations => {
      this.setState(state => ({
        ...state,
        selectedTemplate: template,
        selectedPeriodicity,
        deviations,
      }));
    });
  };

  periodicityChanged = event => {
    const { selectedTemplate } = this.state;
    const periodicityId = event.target.value;
    const periodicity = selectedTemplate.periodicities.find(x => x.id === periodicityId);

    this.setState(state => ({
      ...state,
      selectedPeriodicity: periodicity,
    }));
  };

  isEndDateOutOfRange = endDate => {
    const { planningStart } = this.state;
    if (endDate <= planningStart) return true;
    return false;
  };

  isFormInvalid = () => {
    const {
      planningStart,
      planningStop,
      selectedTemplate,
      selectedPeriodicity,
      scheduleDate,
      action,
    } = this.state;

    if (action === 'add') {
      if (
        !planningStart ||
        !selectedTemplate ||
        !selectedPeriodicity ||
        (selectedPeriodicity.showScheduleDate && !scheduleDate) ||
        !validateDateRange(planningStart, planningStop, true)
      )
        return true;
    } else if (!selectedTemplate || !selectedPeriodicity) return true;

    return false;
  };

  onSave = () => {
    const {
      scheduleDate,
      planningStart,
      planningStop,
      selectedTemplate,
      selectedPeriodicity,
      deletePlannedTasks,
      action,
      deviations,
    } = this.state;

    const { onSave } = this.props;

    let model = {};

    if (action === 'add') {
      model = {
        scheduleDate,
        planningStart,
        planningStop,
        taskTemplateId: selectedTemplate.id,
        periodicityId: selectedPeriodicity.id,
        deviations: deviations.map(x => {
          if (x.reset) {
            return {
              ...x,
              assignedType: null,
            };
          }
          return x;
        }),
      };
    } else {
      model = {
        deletePlannedTasks,
        taskTemplateId: selectedTemplate.id,
        periodicityId: selectedPeriodicity.id,
      };
    }

    return onSave({ action, model });
  };

  assignedToChanged = (deviation, assignedTo) => {
    const { deviations } = this.state;
    this.setState({
      deviations: deviations.map(x => {
        if (x.subtaskTemplateId == deviation.subtaskTemplateId)
          return {
            ...x,
            ...assignedTo,
          };
        return x;
      }),
    });
  };

  executionDurationChanged = (deviation, executionDuration) => {
    const { deviations } = this.state;
    this.setState({
      deviations: deviations.map(x => {
        if (x.subtaskTemplateId !== deviation.subtaskTemplateId || x.isMainTaskDeviation !== deviation.isMainTaskDeviation)
          return x;
        return { ...x, executionDuration };
      }),
    });
  };

  profileLabel = profile => {
    const { employeeProfiles } = this.state;
    var profile = employeeProfiles.find(x => x.id === profile);
    if (!profile) return 'Profile not found';
    return profile.label;
  };

  resetLabel = deviation => {
    const { intl } = this.props;

    const baseMessage = intl.formatMessage(messages.ResetToTemplateSetting);
    let templateMessage = '';

    if (deviation.templateAssignedType === 0) {
      templateMessage = deviation.isMainTaskDeviation
        ? intl.formatMessage(messages.Unassigned).toLowerCase()
        : intl.formatMessage(messages.UserFromMainTask).toLowerCase();
    } else if (deviation.templateAssignedType === 1) {
      templateMessage = this.profileLabel(deviation.templateAssignedProfile).toLowerCase();
    } else if (deviation.templateAssignedType === 2) {
      templateMessage = dataCacheService.getUserName(deviation.templateAssignedUserId);
    } else {
      templateMessage = intl.formatMessage(messages.Unassigned).toLowerCase();
    }

    return `${baseMessage} (${templateMessage.toLocaleLowerCase()})`;
  };

  render() {
    const { show, onClose, intl } = this.props;
    const {
      scheduleDate,
      planningStart,
      planningStop,
      planningStartFocused,
      planningStopFocused,

      templates,
      selectedTemplate,
      selectedPeriodicity,
      action,
      deletePlannedTasks,
      deviations,
      deviationsCollapsed,
    } = this.state;

    const showScheduleDate = (selectedPeriodicity && selectedPeriodicity.showScheduleDate) || false;
    const canCreate = AuthService.hasPermission({
      functionality: functionalities.TaskSchedules,
      privilege: privileges.Create | privileges.Delete,
    });
    const canDelete = AuthService.hasPermission({
      functionality: functionalities.TaskSchedules,
      privilege: privileges.Delete,
    });

    const deviationOnMainTask =
      deviations?.find(x => x.isMainTaskDeviation === true).assignedType !== null;
    if (!show) return null;
    return (
      <OkCancelModal
        title={intl.formatMessage(messages.AddOrRemoveTaskSchedule)}
        show={show}
        onClose={onClose}
        disabled={this.isFormInvalid()}
        onSave={() => this.onSave()}
        size="lg"
      >
        <div className="form-horizontal">
          <div className="form-group">
            <label className="control-label col-sm-4">
              <FormattedMessage {...messages.Action} />
            </label>
            <div className="col-sm-5">
              <select onChange={this.actionChanged} className="form-control" value={action}>
                {canCreate ? (
                  <option value="add">{intl.formatMessage(messages.AddTaskSchedule)}</option>
                ) : null}
                {canDelete ? (
                  <option value="remove">{intl.formatMessage(messages.RemoveTaskSchedule)}</option>
                ) : null}
              </select>
            </div>
          </div>
          <div className="form-group">
            <label className="control-label col-sm-4">
              {intl.formatMessage(messages.TaskTemplate)}
            </label>
            <div className="col-sm-5">
              {templates && (
                <select
                  onChange={this.templateChanged}
                  className="form-control"
                  value={(selectedTemplate && selectedTemplate.id) || ''}
                >
                  {templates.map(template => (
                    <option key={template.id} value={template.id}>
                      {template.label}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>

          <div className="form-group">
            <label className="control-label col-sm-4">
              {intl.formatMessage(messages.Schedule)}
            </label>
            <div className="col-sm-5">
              <select
                onChange={this.periodicityChanged}
                className="form-control"
                value={(selectedPeriodicity && selectedPeriodicity.id) || ''}
              >
                {selectedTemplate &&
                  selectedTemplate.periodicities &&
                  selectedTemplate.periodicities.map(periodicity => (
                    <option key={periodicity.id} value={periodicity.id}>
                      {periodicity.label}
                    </option>
                  ))}
              </select>
            </div>
          </div>

          {canCreate && action === 'add' && (
            <>
              {showScheduleDate && (
                <div className="form-group">
                  <label className="control-label col-sm-4">
                    {intl.formatMessage(messages.Deadline)}
                  </label>
                  <div className="col-sm-5">
                    <DayMonthPicker
                      placeholder={intl.formatMessage(messages.Deadline)}
                      value={scheduleDate}
                      onChange={date => this.setState({ scheduleDate: date })}
                      id="taskScheduleDate"
                      required
                      outputType="moment"
                      block
                      small
                    />
                  </div>
                </div>
              )}
              <div
                className={classNames('form-group', {
                  'has-error': !planningStart,
                })}
              >
                <label className="control-label col-sm-4">
                  {intl.formatMessage(messages.StartPlanning)}
                </label>
                <div className="col-sm-5">
                  <DatePicker
                    placeholder={intl.formatMessage(messages.StartPlanning)}
                    date={planningStart}
                    onDateChange={date =>
                      this.setState({ planningStart: date, planningStop: undefined })
                    }
                    focused={planningStartFocused}
                    onFocusChange={({ focused }) =>
                      this.setState({ planningStartFocused: focused })
                    }
                    id="taskScheduleStartDate"
                    required
                    block
                    small
                    isOutsideRange={isBeforeLastYear}
                  />
                </div>
              </div>
              <div
                className={classNames('form-group', {
                  'has-error': !validateEndDate(planningStop, planningStart),
                })}
              >
                <label className="control-label col-sm-4">
                  {intl.formatMessage(messages.EndPlanning)}
                </label>
                <div className="col-sm-5">
                  <DatePicker
                    placeholder={intl.formatMessage(messages.EndPlanning)}
                    date={planningStop}
                    block
                    small
                    onDateChange={date => this.setState({ planningStop: date })}
                    focused={planningStopFocused}
                    onFocusChange={({ focused }) => this.setState({ planningStopFocused: focused })}
                    id="taskScheduleEndDate"
                    isOutsideRange={d => validateEndDate(planningStart, d)}
                  />
                </div>
              </div>
              <div
                className={classNames('form-group', {
                  'has-error': !validateEndDate(planningStop, planningStart),
                })}
              >
                <div className="col-sm-12">
                  <CollapsablePanel
                    collapsed={deviationsCollapsed}
                    collapseChanged={() =>
                      this.setState({ deviationsCollapsed: !deviationsCollapsed })
                    }
                    title={
                      <span className="margin10L">
                        {intl.formatMessage(messages.ClickToConfigureDeviations)}
                      </span>
                    }
                  >
                    {deviations?.length ? (
                      <>
                        <table
                          className="width100pct"
                          style={{ borderCollapse: 'separate', borderSpacing: '5px 8px' }}
                        >
                          <thead>
                            <tr>
                              <th>{intl.formatMessage(messages.Task)}</th>
                              <th>{intl.formatMessage(messages.ScheduleDeviation)}</th>
                              <th>{intl.formatMessage(messages.AssignedTo)}</th>
                              {AuthService.hasFeature('CapacityPlanning') && (
                                <th>{intl.formatMessage(messages.ExecutionDuration)}</th>
                              )}
                              <th />
                            </tr>
                          </thead>
                          <tbody>
                            {deviations?.map(deviation => (
                              <tr key={deviation.subtaskTemplateId}>
                                <td>
                                  {deviation.isMainTaskDeviation
                                    ? intl.formatMessage(messages.MainTask)
                                    : intl.formatMessage(
                                        listItemMsg(
                                          'taskTemplate',
                                          deviation.subtaskTemplateId,
                                          deviation.subtaskTemplateName,
                                        ),
                                      )}
                                </td>
                                <td>
                                  {deviation.isMainTaskDeviation ? (
                                    '/'
                                  ) : (
                                    <EnumSelectV2
                                      enumType="scheduleDeviationType"
                                      value={deviation.type}
                                      onChange={v => {
                                        this.setState({
                                          deviations: deviations.map(x => {
                                            if (x.subtaskTemplateId !== deviation.subtaskTemplateId)
                                              return x;
                                            return {
                                              ...x,
                                              type: v,
                                            };
                                          }),
                                        });
                                      }}
                                    />
                                  )}
                                </td>
                                <td>
                                  <AssignedToSelector
                                    onChange={assignedTo =>
                                      this.assignedToChanged(deviation, assignedTo)
                                    }
                                    showReset
                                    resetIsInitialValue={true}
                                    resetLabel={this.resetLabel(deviation)}
                                  />
                                </td>
                                {AuthService.hasFeature('CapacityPlanning') && (
                                  <td>
                                    <ExecutionDurationInput
                                      value={deviation.executionDuration}
                                      defaultValue={deviation.templateExecutionDuration}
                                      onChange={v => this.executionDurationChanged(deviation, v)}
                                    />
                                  </td>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {deviationOnMainTask ? (
                          <div className="form-group">
                            <div className="col-sm-12">
                              <div role="alert" className="alert alert-info">
                                <div>
                                  {intl.formatMessage(
                                    messages.TaskScheduleDeviationOnMainParentPropagationToSubtaskExplanation,
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : null}
                      </>
                    ) : (
                      <></>
                    )}
                  </CollapsablePanel>
                </div>
              </div>
            </>
          )}
          {canDelete && action === 'remove' && (
            <div className="form-group">
              <Checkbox
                extraClass="col-sm-offset-4 col-md-8"
                id="deletePlannedTasks"
                value={deletePlannedTasks}
                onClick={() => this.setState({ deletePlannedTasks: !deletePlannedTasks })}
                text={intl.formatMessage(messages.DeletePlannedTasks)}
                title={intl.formatMessage(messages.DeletePlannedTasksTooltip)}
              />
            </div>
          )}
        </div>
      </OkCancelModal>
    );
  }
}
